/**
 * 1b · LOGGED-IN HOME — full criminal daily-docket preview for the landing.
 * MARKETING-STATIC (see previewData.ts). Mirrors the live dashboard
 * (2026-11-03): greeting + docket line, Cause List week strip + Urgent
 * Deadlines (liberty clocks, then filing deadlines), then the Court updates
 * strip. Nothing is shown here that the product does not show.
 */
import PreviewFrame from './PreviewChrome';
import {
  CRIMINAL_NAV,
  CRIMINAL_RAIL_USER,
  CRIMINAL_DOCKET_LINE,
  CRIMINAL_WEEK_LABEL,
  CRIMINAL_WEEK,
  CRIMINAL_CAUSE_LIST,
  CRIMINAL_LIBERTY,
  CRIMINAL_DEADLINES,
  CRIMINAL_NOT_ASSESSED,
  CRIMINAL_COURT_UPDATES,
} from './previewData';

const CHIP = 'font-plex text-[8px] tracking-[0.06em] px-1.5 py-0.5 border';
const DARK_TAG =
  'font-plex text-[8px] tracking-[0.1em] text-brass border border-brass/40 px-1.5 py-0.5 shrink-0';
const DARK_ROW = 'grid grid-cols-[2.25rem_1fr_auto] gap-3 px-4 py-3 items-center';

const DaysFigure = ({ days }: { days: string }) => (
  <span className="font-display text-[20px] leading-none text-brass">
    {days}
    <span className="text-[10px] text-chamber-muted-faint">d</span>
  </span>
);

export default function CriminalHomePreview() {
  const d = CRIMINAL_DOCKET_LINE;
  const firstName = CRIMINAL_RAIL_USER.name.split(' ')[0];
  return (
    <PreviewFrame
      label="1b · LOGGED-IN HOME"
      nav={CRIMINAL_NAV}
      railUser={CRIMINAL_RAIL_USER}
      searchPlaceholder="Search anything…"
    >
      {/* greeting + docket line */}
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-parchment-border-strong pb-3">
        <div>
          <h3 className="font-display text-[22px] leading-none text-chamber-panel">
            Good evening, {firstName} —{' '}
            <em className="font-editorial italic text-brass-ink">Criminal Practice</em>
          </h3>
          <div className="text-[11px] text-parchment-muted-warm mt-1.5">
            <span className="font-semibold text-chamber-panel">{d.hearings}</span> hearings this
            week · next hearing{' '}
            <span className="font-semibold text-chamber-panel">{d.nextHearing}</span> ·{' '}
            <span className="font-semibold text-chamber-panel">{d.urgent}</span> urgent ·{' '}
            <span className="font-semibold text-chamber-panel">{d.theories}</span> live theories ·{' '}
            {d.date}
          </div>
        </div>
        <span className="font-plex text-[9px] tracking-[0.25em] text-parchment-muted-faint">
          DAILY DOCKET
        </span>
      </div>

      {/* cause list + urgent deadlines */}
      <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[1.5fr_1fr] gap-4">
        <div className="flex flex-col border border-parchment-border-soft bg-parchment-card">
          <div className="flex items-center justify-between px-4 py-3 border-b border-parchment-border-soft">
            <span className="font-display text-[15px] text-chamber-panel">Cause List</span>
            <span className="font-plex text-[9px] tracking-[0.08em] text-brass-ink">
              {CRIMINAL_WEEK_LABEL}
            </span>
          </div>

          {/* week strip */}
          <div className="flex items-center gap-1 px-3 pt-3 pb-2.5 border-b border-parchment-border-soft">
            <span className="w-4 text-center text-[11px] text-parchment-muted-faint">‹</span>
            <div className="flex-1 grid grid-cols-7 gap-1">
              {CRIMINAL_WEEK.map((w) => (
                <div
                  key={w.date}
                  className={`flex flex-col items-center py-1 border ${
                    w.selected
                      ? 'bg-chamber-panel border-chamber-panel text-parchment'
                      : w.today
                        ? 'border-brass text-chamber-panel'
                        : 'border-transparent text-chamber-panel'
                  }`}
                >
                  <span
                    className={`font-plex text-[7.5px] tracking-[0.08em] ${
                      w.selected ? 'text-parchment/80' : 'text-parchment-muted'
                    }`}
                  >
                    {w.day}
                  </span>
                  <span className="font-display text-[15px] leading-tight tabular-nums">{w.date}</span>
                  <span
                    className={`mt-0.5 h-3.5 min-w-[0.9rem] px-1 font-plex text-[8px] leading-[14px] text-center tabular-nums ${
                      w.count > 0
                        ? w.selected
                          ? 'bg-brass text-chamber-ink'
                          : 'bg-brass/15 text-brass-deep'
                        : 'text-transparent'
                    }`}
                  >
                    {w.count > 0 ? w.count : '·'}
                  </span>
                </div>
              ))}
            </div>
            <span className="w-4 text-center text-[11px] text-chamber-panel">›</span>
          </div>

          {/* today's hearings */}
          <div className="flex-1 divide-y divide-parchment-border-soft">
            {CRIMINAL_CAUSE_LIST.map((c) => (
              <div key={c.title} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-3">
                <div className="min-w-0">
                  <div className="font-editorial italic text-[13.5px] text-chamber-panel truncate">
                    {c.title}
                  </div>
                  <div className="font-plex text-[8.5px] text-parchment-muted-faint mt-0.5">{c.sub}</div>
                </div>
                <span className={`${CHIP} text-brass-deep border-brass-border shrink-0`}>{c.chip}</span>
                <span className="hidden md:inline font-plex text-[8px] text-parchment-muted max-w-[7rem] truncate shrink-0">
                  {c.court}
                </span>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 border-t border-parchment-border-soft">
            <span className="font-plex text-[9px] tracking-[0.08em] text-brass-ink">Full cause list →</span>
          </div>
        </div>

        <div className="flex flex-col border border-chamber-panel bg-chamber-rail">
          <div className="flex items-center justify-between px-4 py-3 border-b border-brass-hairline">
            <span className="font-display text-[15px] text-parchment">Urgent Deadlines</span>
            <span className="font-plex text-[9px] tracking-[0.1em] text-brass">NEXT 30 DAYS</span>
          </div>

          <div className="divide-y divide-brass-hairline-faint">
            {CRIMINAL_LIBERTY.map((r) =>
              r.kind === 'exposed' ? (
                <div key={r.headline} className={DARK_ROW}>
                  <DaysFigure days={r.days} />
                  <div className="min-w-0">
                    <div className="text-[11.5px] font-semibold text-parchment leading-snug">
                      {r.headline}
                    </div>
                    <div className="font-editorial italic text-[10.5px] text-chamber-muted-dim mt-0.5">
                      {r.caseLine}
                    </div>
                  </div>
                  <span />
                </div>
              ) : (
                <div key={r.headline} className={DARK_ROW}>
                  <span className="font-display text-[20px] leading-none text-brass/50">—</span>
                  <div className="min-w-0">
                    <div className="text-[11.5px] font-semibold text-parchment leading-snug">
                      {r.headline}
                    </div>
                    <div className="font-editorial italic text-[10.5px] text-chamber-muted-dim mt-0.5">
                      {r.caseLine}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      <span className={`${CHIP} text-brass border-brass/40`}>FILED</span>
                      <span className={`${CHIP} text-chamber-muted border-chamber-border`}>NOT FILED</span>
                      <span className={`${CHIP} text-chamber-muted-dim border-chamber-border`}>
                        DON’T KNOW
                      </span>
                    </div>
                  </div>
                  <span className={DARK_TAG}>ANSWER</span>
                </div>
              ),
            )}
            {CRIMINAL_DEADLINES.map((dl) => (
              <div key={dl.what} className={DARK_ROW}>
                <DaysFigure days={dl.days} />
                <div className="min-w-0">
                  <div className="text-[11.5px] font-semibold text-parchment leading-snug truncate">
                    {dl.what}
                  </div>
                  <div className="font-editorial italic text-[10.5px] text-chamber-muted-dim mt-0.5 truncate">
                    {dl.matter}
                  </div>
                </div>
                <span className={DARK_TAG}>{dl.tag}</span>
              </div>
            ))}
          </div>

          <div className="mt-auto">
            <div className="px-4 py-2.5 border-t border-brass-hairline-faint text-[9.5px] leading-relaxed text-chamber-muted-faint">
              {CRIMINAL_NOT_ASSESSED}{' '}
              <span className="font-plex text-[8px] tracking-[0.08em] text-brass">SHOW</span>
            </div>
            <div className="px-4 py-2.5 border-t border-brass-hairline-faint font-plex text-[8px] tracking-[0.08em] text-chamber-muted-faint">
              QUIET LIBERTY ALERTS — THIS SESSION ONLY
            </div>
          </div>
        </div>
      </div>

      {/* court updates strip */}
      <div className="border border-parchment-border-soft bg-parchment-card p-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-display text-[15px] text-chamber-panel inline-flex items-center gap-2">
            <span className="text-brass-ink text-[12px]">⚖</span>
            Court updates
            <span className="font-plex text-[8px] tracking-[0.1em] text-parchment-muted-faint">
              VIA ECOURTS
            </span>
          </span>
          <span className="text-parchment-muted-faint text-[12px]">✕</span>
        </div>
        <div className="divide-y divide-parchment-border-soft">
          {CRIMINAL_COURT_UPDATES.map((u) => (
            <div key={u.meta} className="flex items-start gap-3 py-2">
              <span className="text-brass-ink text-[11px] mt-0.5 shrink-0">⚖</span>
              <div className="min-w-0">
                <div className="text-[11.5px] text-chamber-panel leading-snug">
                  {u.lead && <span className="font-medium text-brass-ink">{u.lead} · </span>}
                  {u.summary}
                </div>
                <div className="text-[9.5px] text-parchment-muted-warm mt-0.5 truncate">{u.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PreviewFrame>
  );
}
